import { Injectable } from '@angular/core';
import { Observable, of, map } from 'rxjs';
import { GlobalConnSettings, GlobalSettingsData } from 'src/app/shared/global-settings-data';
import { AppConfigService } from 'src/app/services/AppConfigService';
import { KeycloakService } from 'keycloak-angular';
import { UserConfigService } from 'src/app/services/user-config.service';

/**
 * Central store for application-wide connection settings and the active
 * user's database profile.
 *
 * This service is the single source of truth for two independent concerns:
 *
 * **1. Connection settings** (`GlobalConnSettings`) — addresses of the API
 * server, Keycloak auth server, SQL Server, and jsReport server. Values
 * originate from `app.config.json` (via {@link AppConfigService}) and may be
 * overridden per-session in `localStorage` by the Setup screen.
 *
 * **2. User DB profile** (`GlobalSettingsData`) — per-user preferences such
 * as the active company, stock warehouses, departments, currencies, and
 * access group. Values are loaded asynchronously from the SQL Server by
 * {@link UserConfigService} after Keycloak login and are cached in
 * `localStorage` for synchronous access.
 *
 * Every API service injects this service to obtain the custom Webyto HTTP
 * headers sent with every backend request (DB server, DB name, DB user,
 * access group). Use {@link getConnectionOptions} to build those headers.
 *
 * @remarks
 * **Initialisation order matters.** `AppConfigService` must have completed its
 * `APP_INITIALIZER` sequence before any connection getter is called.
 * `UserConfigService` must have resolved the DB profile before synchronous
 * getters such as {@link getGlobalSettings} return real values. Components
 * that need reactive updates should subscribe to
 * {@link getObservableGlobalSettings} instead.
 */
@Injectable({
  providedIn: 'root'
})
export class GetglobalsettingsService {
  private _accessGroup: number;
  private _globalSettingsData: GlobalSettingsData = null;
  private _loggedUserName: string = 'defaultProfile';
  private _globalConnection: GlobalConnSettings = null;
  private _loggedConnName: string = 'defaultConnection';

  constructor(
    private appConfigService: AppConfigService,
    private auth: KeycloakService,
    private userConfigService: UserConfigService,
  ) {
    // Don't initialize from config in constructor - config isn't loaded yet!
    // Initialize with empty/default values instead
  }

  // ---------------------------------------------------------------------------
  // Internal helpers
  // ---------------------------------------------------------------------------

  /**
   * Populates `_globalSettingsData` from `app.config.json` defaults on first
   * call. No-op if the data is already initialised or if the config has not
   * been loaded yet.
   */
  private initializeFromConfig(): void {
    const config = this.appConfigService.getConfig();

    if (config && config.defaultCompanySettings && !this._globalSettingsData) {
      this._globalSettingsData = new GlobalSettingsData({
        accessGroup: 0,
        mainCompanyId: config.defaultCompanySettings.defMainCompanyId,
        mainPersonId: config.defaultCompanySettings.defMainPersonId,
        vatValue: config.defaultCompanySettings.defVatValue,
        mainCurrencyId: config.defaultCompanySettings.defMainCurrencyId,
        balanceCurrencyId: config.defaultCompanySettings.defBalanceCurrencyId,
        viewDCurrencyId: config.defaultCompanySettings.defViewDCurrencyId,
        viewECurrencyId: config.defaultCompanySettings.defViewECurrencyId,
        mainStockId: config.defaultCompanySettings.defMainStockId,
        impStockId: config.defaultCompanySettings.defImpStockId,
        auxStockId: config.defaultCompanySettings.defAuxStockId,
        impCountFlag: config.defaultCompanySettings.defImpCountFlag,
        auxCountFlag: config.defaultCompanySettings.defAuxCountFlag,
        manDepartmentId: config.defaultCompanySettings.defManDepartmentId,
        auxDepartmentId: config.defaultCompanySettings.defAuxDepartmentId,
        paidDepartmentId: config.defaultCompanySettings.defPaidDepartmentId,
        cashDepartmentId: config.defaultCompanySettings.defCashDepartmentId,
        defaultTradeCompanyId: 0,
        urlPrefixString: '',
      });
    }
  }

  /**
   * Normalises legacy `app.config.json` key names to the flat address fields
   * used internally (`apiSrvAddress`, `dbSrvAddress`, `authSrvAddress`,
   * `reportSrvAddress`).
   *
   * Older config files used nested objects (`apiServer.url`, `dbServer.url`,
   * etc.). This shim ensures both old and new formats are accepted without
   * requiring a deployment change.
   *
   * @param conn - Connection settings object to normalise in-place.
   * @returns The same object with the flat address fields populated.
   */
  private applyBackwardCompatibility(conn: GlobalConnSettings): GlobalConnSettings {
    if ('apiServer' in conn) {
      conn.apiSrvAddress = conn.apiServer.url;
    }
    if ('dbServer' in conn) {
      conn.dbSrvAddress = conn.dbServer.url;
    }
    if ('authServer' in conn) {
      conn.authSrvAddress = conn.authServer.url;
    }
    if ('reportServer' in conn) {
      conn.reportSrvAddress = conn.reportServer.url1;
    }
    return conn;
  }

  // ---------------------------------------------------------------------------
  // Global settings (user DB profile)
  // ---------------------------------------------------------------------------

  /**
   * Returns the active user's DB profile synchronously.
   *
   * Resolution priority:
   * 1. In-memory cache from {@link UserConfigService} (populated after the
   *    async DB load completes).
   * 2. `localStorage` entry keyed by the logged-in username.
   * 3. Defaults from `app.config.json`.
   *
   * @returns The current `GlobalSettingsData`, or `null` if the
   *   `APP_INITIALIZER` sequence has not yet completed.
   */
  getGlobalSettings(): GlobalSettingsData {
    // Prefer the already-loaded DB profile from UserConfigService when available,
    // so synchronous callers get the real values after the first async load.
    const cached = this.userConfigService.getDbSettings();
    if (cached) {
      this._globalSettingsData = cached;
      return this._globalSettingsData;
    }

    this.initializeFromConfig();

    const _storedSettingsData: string = localStorage.getItem(this._loggedUserName);
    if (_storedSettingsData) {
      this._globalSettingsData = new GlobalSettingsData(JSON.parse(_storedSettingsData));
    }

    return this._globalSettingsData;
  }

  /**
   * Returns an Observable of the user's DB profile that emits only after the
   * full initialisation sequence (Keycloak login + DB settings fetch) has
   * completed.
   *
   * Delegates to {@link UserConfigService.getObservableDbSettings} and keeps
   * the local cache and `localStorage` in sync on each emission.
   *
   * Prefer this over {@link getGlobalSettings} in components that need to
   * react to the profile becoming available or changing.
   *
   * @returns Observable that emits `GlobalSettingsData` once fully loaded.
   */
  getObservableGlobalSettings(): Observable<GlobalSettingsData> {
    return this.userConfigService.getObservableDbSettings().pipe(
      map(dbSettings => {
        // Keep the local cache and localStorage in sync
        this._globalSettingsData = dbSettings;
        localStorage.setItem(this._loggedUserName, JSON.stringify(dbSettings));
        return dbSettings;
      }),
    );
  }

  /**
   * Persists updated DB profile settings to `localStorage` and updates the
   * in-memory cache. Does not push changes back to the SQL Server.
   *
   * @param e - The updated `GlobalSettingsData` to store.
   */
  setGlobalSettings(e: GlobalSettingsData): void {
    localStorage.setItem(this._loggedUserName, JSON.stringify(e));
    this._globalSettingsData = new GlobalSettingsData(e);
  }

  /**
   * Returns the user's current access group ID, used as the
   * `Webyto-Access-Group` HTTP header on every API request.
   * Falls back to `0` if the profile has not yet been loaded.
   */
  getAccessGroup(): number {
    this.initializeFromConfig();
    return this._globalSettingsData ? this._globalSettingsData.accessGroup : 0;
  }

  /**
   * Returns the VAT rate (%) from the active user profile.
   * Falls back to `20` (Ukrainian standard rate) if the profile is not loaded.
   */
  getVAT(): number {
    this.initializeFromConfig();
    return this._globalSettingsData ? this._globalSettingsData.vatValue : 20;
  }

  /**
   * Returns the main company ID from the active user profile.
   * Falls back to `0` if the profile is not loaded.
   */
  getMainCompanyId(): number {
    this.initializeFromConfig();
    return this._globalSettingsData?.mainCompanyId || 0;
  }

  /**
   * Returns the main currency ID from the active user profile.
   * Falls back to `0` if the profile is not loaded.
   */
  getMainCurrencyId(): number {
    this.initializeFromConfig();
    return this._globalSettingsData?.mainCurrencyId || 0;
  }

  /**
   * Returns the balance currency ID from the active user profile.
   * Falls back to `0` if the profile is not loaded.
   */
  getBalanceCurrencyId(): number {
    this.initializeFromConfig();
    return this._globalSettingsData?.balanceCurrencyId || 0;
  }

  // ---------------------------------------------------------------------------
  // Connection settings
  // ---------------------------------------------------------------------------

  /**
   * Returns connection settings sourced directly from `app.config.json`
   * without applying any `localStorage` overrides.
   *
   * Used during the `APP_INITIALIZER` bootstrap sequence to get the canonical
   * deployment configuration before any user-specific overrides are applied.
   *
   * @returns Default `GlobalConnSettings` from the config file, or an empty
   *   object if the config has not yet been loaded.
   */
  getDefaultConnSettings(): GlobalConnSettings {
    const config = this.appConfigService.getConfig();

    if (!config || !config.connection) {
      console.warn('GetglobalsettingsService: Config not loaded yet');
      return {} as GlobalConnSettings;
    }

    this._globalConnection = this.applyBackwardCompatibility({ ...config.connection });
    return this._globalConnection;
  }

  /**
   * Wraps {@link getDefaultConnSettings} in an Observable for callers that
   * require a reactive interface during bootstrap.
   */
  getObservableDefaultConnSettings(): Observable<GlobalConnSettings> {
    return of(this.getDefaultConnSettings());
  }

  /**
   * Returns the active connection settings, preferring any session override
   * stored in `localStorage` over the config-file defaults.
   *
   * The Setup screen writes overrides to `localStorage`; this method ensures
   * those overrides are picked up by all subsequent API calls without a page
   * reload.
   *
   * @returns The merged `GlobalConnSettings` — localStorage override if
   *   present, otherwise `app.config.json` defaults.
   */
  getConnSettings(): GlobalConnSettings {
    const _storedConnSettings: string = localStorage.getItem(this._loggedConnName);

    if (_storedConnSettings) {
      this._globalConnection = this.applyBackwardCompatibility(JSON.parse(_storedConnSettings));
    }

    return this._globalConnection || this.getDefaultConnSettings();
  }

  /**
   * Wraps {@link getConnSettings} in an Observable for callers that require a
   * reactive interface.
   */
  getObservableLocalConnSettings(): Observable<GlobalConnSettings> {
    return of(this.getConnSettings());
  }

  /**
   * Persists a connection settings override to `localStorage` and updates the
   * in-memory cache. Subsequent calls to {@link getConnSettings} will return
   * these values.
   *
   * @param e - The connection settings to store as the session override.
   */
  setConnSettings(e: GlobalConnSettings): void {
    localStorage.setItem(this._loggedConnName, JSON.stringify(e));
    this._globalConnection = e;
  }

  // ---------------------------------------------------------------------------
  // Individual connection getters (with backward compatibility fallbacks)
  // ---------------------------------------------------------------------------

  /**
   * Returns the base URL of the backend REST API server
   * (e.g. `https://api.example.com`). Used as the URL prefix by all API
   * services.
   */
  getApiServerUrl(): string {
    const conn = this.getConnSettings();
    return conn?.apiSrvAddress || conn?.apiServer?.url || '';
  }

  /**
   * Returns the base URL of the Keycloak authentication server
   * (e.g. `https://sara.disti.pro:8443/auth/`).
   */
  getAuthServerUrl(): string {
    const conn = this.getConnSettings();
    return conn?.authSrvAddress || conn?.authServer?.url || '';
  }

  /**
   * Returns the Keycloak realm name. Falls back to `'portal'` if the config
   * does not specify one.
   */
  getKeycloakRealm(): string {
    return this.getConnSettings()?.authServer?.realm || 'portal';
  }

  /**
   * Returns the base URL of the jsReport server used for PDF generation.
   */
  getReportServerUrl(): string {
    const conn = this.getConnSettings();
    return conn?.reportSrvAddress || conn?.reportServer?.url1 || '';
  }

  /**
   * Returns the SQL Server host address sent as the `Webyto-Database-Server`
   * header. The backend uses this key to select the correct connection pool
   * from `DB_CREDENTIALS`.
   */
  getDbServerUrl(): string {
    const conn = this.getConnSettings();
    return conn?.dbSrvAddress || conn?.dbServer?.url || '';
  }

  /**
   * Returns the database name sent as the `Webyto-Database-Name` header.
   */
  getDatabaseName(): string {
    const conn = this.getConnSettings();
    return conn?.dbServer?.dbName || conn?.dataBaseName || '';
  }

  /**
   * Returns the database login name sent as the `Webyto-Database-User` header.
   * Informational only — the backend resolves credentials server-side from
   * `DB_CREDENTIALS` using the server key, not this value.
   */
  getDbUserName(): string {
    const conn = this.getConnSettings();
    return conn?.dbServer?.userLogin || conn?.dbUserLogin || '';
  }

  /**
   * Builds the custom HTTP headers object required by every protected backend
   * request.
   *
   * The backend uses these headers to route the request to the correct SQL
   * Server instance and database, and to apply access-group filtering.
   *
   * @returns An object with the four Webyto connection headers:
   *   - `Webyto-Database-Server` — SQL Server host key from `DB_CREDENTIALS`
   *   - `Webyto-Database-Name` — target database name
   *   - `Webyto-Database-User` — informational login name
   *   - `Webyto-Access-Group` — user's security group ID as a string
   */
  getConnectionOptions(): any {
    return {
      'Webyto-Database-Server': this.getDbServerUrl(),
      'Webyto-Database-Name': this.getDatabaseName(),
      'Webyto-Database-User': this.getDbUserName(),
      'Webyto-Access-Group': this.getAccessGroup().toString(),
    };
  }

  /**
   * Builds the standard HTTP header set used by most API service call sites:
   * the four {@link getConnectionOptions} connection headers plus the main
   * company, timezone offset, and content-type headers every protected
   * request also needs.
   *
   * Resolved fresh on every call, like {@link getConnSettings} — so a
   * Setup-screen override or a company switch takes effect on the very next
   * request without requiring a page reload.
   *
   * @returns An object with all seven standard Webyto request headers.
   */
  getStandardApiHeaders(): any {
    return {
      ...this.getConnectionOptions(),
      'Webyto-Main-Company': this.getMainCompanyId().toString(),
      'Webyto-TimezoneOffset': (new Date()).getTimezoneOffset().toString(),
      'Content-Type': 'application/json',
    };
  }

  /**
   * Returns `true` if the currently logged-in Keycloak user holds **at least
   * one** of the specified realm roles.
   *
   * @param roles - One or more Keycloak realm role names to check.
   * @returns `false` if the user is not logged in or holds none of the roles.
   */
  hasSomeRoles(...roles: string[]): boolean {
    if (!this.auth.isLoggedIn()) {
      return false;
    }
    const userRoles = this.auth.getUserRoles();
    return roles.some(role => userRoles.includes(role));
  }

  /**
   * Returns `true` if the currently logged-in Keycloak user holds **all** of
   * the specified realm roles.
   *
   * @param roles - One or more Keycloak realm role names to check.
   * @returns `false` if the user is not logged in or is missing any of the
   *   roles.
   */
  hasAllRoles(...roles: string[]): boolean {
    if (!this.auth.isLoggedIn()) {
      return false;
    }
    const userRoles = this.auth.getUserRoles();
    return roles.every(role => userRoles.includes(role));
  }
}
